import React, { useMemo } from 'react';
import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import SearchableSelect from 'shared/components/SearchableSelect';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { DEPLOYMENT_STATUS, DEPLOYMENT_ENVIRONMENT, formatEnumLabel } from '../constants/enums';

/** Maps enum value → API / form date key */
export const DEPLOYMENT_STATUS_DATE_KEYS = {
  PLANNED: 'planned',
  DEVELOPMENT: 'development',
  TESTING: 'testing',
  PILOT: 'pilot',
  PRODUCTION: 'production',
  RETIRED: 'retired',
  SUSPENDED: 'suspended',
};

export const emptyDeploymentStatusDates = () => ({
  planned: '',
  development: '',
  testing: '',
  pilot: '',
  production: '',
  retired: '',
  suspended: '',
});

export const emptyDeploymentStatusApprovals = () => ({
  planned: [],
  development: [],
  testing: [],
  pilot: [],
  production: [],
  retired: [],
  suspended: [],
});

export const emptyDeploymentApprovalRow = () => ({
  key: `dsa-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  id: null,
  name: '',
  email: '',
  approvedAt: '',
  decision: '',
  comment: '',
});

export function dateKeyForStatus(status) {
  return DEPLOYMENT_STATUS_DATE_KEYS[status] || null;
}

export function statusForDateKey(key) {
  if (!key) return null;
  return (
    Object.entries(DEPLOYMENT_STATUS_DATE_KEYS).find(([, dateKey]) => dateKey === key)?.[0] || null
  );
}

export function toDatetimeLocalValue(value) {
  if (!value) return '';
  const raw = String(value).trim();
  if (!raw) return '';
  const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T');
  return normalized.length >= 16 ? normalized.slice(0, 16) : normalized;
}

/** Map API deploymentStatuses[] → form approvals-by-stage-key object. */
export function mapDeploymentStatusApprovalsFromApi(deploymentStatuses = []) {
  const approvals = emptyDeploymentStatusApprovals();
  (deploymentStatuses ?? []).forEach((stage) => {
    const key = dateKeyForStatus(stage?.status);
    if (!key) return;
    approvals[key] = (stage.approvals ?? []).map((a) => ({
      ...emptyDeploymentApprovalRow(),
      id: a.id ?? null,
      name: a.name ?? '',
      email: a.email ?? '',
      approvedAt: toDatetimeLocalValue(a.approvedAt),
      decision: a.decision ?? '',
      comment: a.comment ?? '',
    }));
  });
  return approvals;
}

const STAGE_HINTS = {
  PLANNED: 'When this system was planned',
  DEVELOPMENT: 'When development started',
  TESTING: 'When testing started',
  PILOT: 'When the pilot began',
  PRODUCTION: 'When it went live',
  RETIRED: 'When it was retired',
  SUSPENDED: 'When it was suspended',
};

/**
 * Current deployment status + date for every lifecycle stage.
 * @param {'none'|'list'|'quickButtons'} approvalUiMode
 *   - none: timeline dates only (Software Identification)
 *   - list: free-form approvals under each stage
 *   - quickButtons: "1st Approval" / "2nd Approval" only — no stage dates (Approval & Sign-off)
 */
export default function DeploymentLifecycleFields({
  deploymentStatus,
  deploymentEnvironment,
  deploymentStatusDates,
  deploymentStatusApprovals,
  onStatusChange,
  onEnvironmentChange,
  onDateChange,
  onApprovalsChange,
  /** Optional: open dialog / persist 1st/2nd approval (Approval & Sign-off). */
  onQuickApprovalPersist,
  /** When true with quickButtons, click opens dialog via onQuickApprovalRequest instead of auto-save. */
  onQuickApprovalRequest,
  error,
  dateError,
  readOnly = false,
  approvalUiMode = 'none',
  currentUser = null,
  /** Match Software Identification compact sizing (280px / small). */
  compactControls = false,
  controlWidth = 280,
  /** Rendered immediately after Deployment Environment (before the timeline). */
  afterEnvironment = null,
}) {
  const theme = useTheme();
  const dates = deploymentStatusDates || emptyDeploymentStatusDates();
  const approvalsByStage = deploymentStatusApprovals || emptyDeploymentStatusApprovals();
  const currentIndex = DEPLOYMENT_STATUS.indexOf(deploymentStatus);
  const useQuickButtons = approvalUiMode === 'quickButtons';
  const useApprovalList = approvalUiMode === 'list';

  const primary = theme.palette.primary.main;
  const secondary = theme.palette.secondary.main;
  const isDark = theme.palette.mode === 'dark';

  const filledCount = useMemo(
    () => DEPLOYMENT_STATUS.filter((s) => Boolean(dates[dateKeyForStatus(s)])).length,
    [dates]
  );

  const nowDatetimeLocal = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const updateStageApprovals = (key, nextList) => {
    onApprovalsChange?.({
      ...approvalsByStage,
      [key]: nextList,
    });
  };

  const addApproval = (key) => {
    updateStageApprovals(key, [...(approvalsByStage[key] || []), emptyDeploymentApprovalRow()]);
  };

  const patchApproval = (key, approvalKey, patch) => {
    updateStageApprovals(
      key,
      (approvalsByStage[key] || []).map((row) =>
        row.key === approvalKey ? { ...row, ...patch } : row
      )
    );
  };

  const removeApproval = (key, approvalKey) => {
    updateStageApprovals(
      key,
      (approvalsByStage[key] || []).filter((row) => row.key !== approvalKey)
    );
  };

  const setQuickApproval = async (key, slotIndex) => {
    const name = (currentUser?.name || '').trim();
    const email = (currentUser?.email || '').trim();
    if (!name && !email) return;

    const status = statusForDateKey(key);
    if (!status) return;

    if (onQuickApprovalRequest) {
      const existing = (approvalsByStage[key] || [])[slotIndex];
      onQuickApprovalRequest({
        status,
        slot: slotIndex + 1,
        name: name || email,
        email: email || '',
        stageDate: dates[key] || undefined,
        existingComment: existing?.comment || '',
        existingDecision: existing?.decision || '',
      });
      return;
    }

    const approvedAt = nowDatetimeLocal();
    const list = [...(approvalsByStage[key] || [])];
    while (list.length <= slotIndex) {
      list.push(emptyDeploymentApprovalRow());
    }
    const existing = list[slotIndex] || emptyDeploymentApprovalRow();
    list[slotIndex] = {
      ...existing,
      key: existing.key || emptyDeploymentApprovalRow().key,
      id: existing.id ?? null,
      name: name || email,
      email: email || '',
      approvedAt,
      decision: 'APPROVED',
      comment: existing.comment || '',
    };
    updateStageApprovals(key, list);

    if (onQuickApprovalPersist) {
      await onQuickApprovalPersist({
        status,
        slot: slotIndex + 1,
        name: name || email,
        email: email || '',
        approvedAt,
        stageDate: dates[key] || undefined,
        decision: 'APPROVED',
        comment: '',
      });
    }
  };

  const isSlotApproved = (stageApprovals, slotIndex) => {
    const row = stageApprovals[slotIndex];
    return Boolean(
      row
        && ((row.name || '').trim() || (row.email || '').trim())
        && row.approvedAt
        && row.decision !== 'DECLINED'
        && (row.decision === 'APPROVED' || !row.decision)
    );
  };

  const isSlotDeclined = (stageApprovals, slotIndex) => {
    const row = stageApprovals[slotIndex];
    return Boolean(row && row.decision === 'DECLINED');
  };

  return (
    <Box>
      <Box
        display="flex"
        gap={2}
        flexWrap="wrap"
        flexDirection={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
        sx={{ mb: 2.5 }}
      >
        <Box
          sx={
            compactControls
              ? { width: { xs: '100%', sm: controlWidth }, flexShrink: 0 }
              : { flex: 1, width: '100%' }
          }
        >
          <SearchableSelect
            required
            size={compactControls ? 'small' : 'medium'}
            label="Current deployment status"
            value={deploymentStatus || ''}
            onChange={(next) => onStatusChange?.(next)}
            disabled={readOnly}
            error={Boolean(error)}
            helperText={error || undefined}
            options={DEPLOYMENT_STATUS.map((status) => ({
              value: status,
              label: formatEnumLabel(status),
            }))}
          />
        </Box>

        <Box
          sx={
            compactControls
              ? { width: { xs: '100%', sm: controlWidth }, flexShrink: 0 }
              : { flex: 1, width: '100%' }
          }
        >
          <SearchableSelect
            size={compactControls ? 'small' : 'medium'}
            label="Deployment Environment"
            value={deploymentEnvironment || ''}
            onChange={(next) => onEnvironmentChange?.(next)}
            disabled={readOnly}
            options={DEPLOYMENT_ENVIRONMENT.map((env) => ({
              value: env,
              label: formatEnumLabel(env),
            }))}
          />
        </Box>

        {afterEnvironment ? (
          <Box sx={{ width: '100%' }}>
            {afterEnvironment}
          </Box>
        ) : null}
      </Box>

      <Box
        sx={{
          borderRadius: 2,
          border: `1px solid ${alpha(primary, isDark ? 0.35 : 0.14)}`,
          bgcolor: isDark ? 'background.paper' : alpha(primary, 0.03),
          p: { xs: 1.5, sm: 2 },
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="baseline" mb={1.5} gap={1} flexWrap="wrap">
          <Box>
            <Typography variant="subtitle2" fontWeight={700}>
              Deployment timeline
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {useQuickButtons
                ? 'Approve each stage with the logged-in user (1st and 2nd approval).'
                : 'Record the date for each stage. The selected status should have a date.'}
            </Typography>
          </Box>
          {!useQuickButtons ? (
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {filledCount}/{DEPLOYMENT_STATUS.length} dated
            </Typography>
          ) : null}
        </Box>

        <Box display="flex" flexDirection="column" gap={0}>
          {DEPLOYMENT_STATUS.map((status, index) => {
            const key = dateKeyForStatus(status);
            const isCurrent = status === deploymentStatus;
            const hasDate = Boolean(dates[key]);
            const isPast = currentIndex >= 0 && index < currentIndex;
            const showDateError = isCurrent && Boolean(dateError);
            const stageApprovals = approvalsByStage[key] || [];

            return (
              <Box
                key={status}
                display="flex"
                gap={1.5}
                alignItems="stretch"
                sx={{
                  position: 'relative',
                  py: 1.25,
                  px: 1,
                  borderRadius: 1.5,
                  bgcolor: isCurrent ? alpha(secondary, isDark ? 0.18 : 0.1) : 'transparent',
                  border: isCurrent
                    ? `1px solid ${alpha(secondary, isDark ? 0.55 : 0.35)}`
                    : '1px solid transparent',
                  transition: 'background-color 0.15s ease, border-color 0.15s ease',
                }}
              >
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  sx={{ width: 28, flexShrink: 0, pt: 0.75 }}
                >
                  {hasDate || isCurrent ? (
                    <CheckCircleIcon
                      sx={{
                        fontSize: 22,
                        color: isCurrent ? secondary : theme.palette.success.main,
                      }}
                    />
                  ) : (
                    <RadioButtonUncheckedIcon
                      sx={{ fontSize: 22, color: alpha(theme.palette.text.secondary, 0.45) }}
                    />
                  )}
                  {index < DEPLOYMENT_STATUS.length - 1 ? (
                    <Box
                      sx={{
                        flex: 1,
                        width: 2,
                        mt: 0.5,
                        mb: -1.25,
                        minHeight: 28,
                        bgcolor: isPast || hasDate
                          ? alpha(theme.palette.success.main, 0.55)
                          : alpha(theme.palette.text.secondary, 0.2),
                      }}
                    />
                  ) : null}
                </Box>

                <Box flex={1} minWidth={0} display="flex" flexDirection="column" gap={1}>
                  <Box
                    display="flex"
                    flexDirection={{ xs: 'column', sm: 'row' }}
                    gap={1.25}
                    alignItems={{ sm: 'center' }}
                  >
                    <Box flex={1} minWidth={140}>
                      <Typography
                        variant="body2"
                        fontWeight={isCurrent ? 700 : 600}
                        sx={{ color: isCurrent ? secondary : 'text.primary', lineHeight: 1.3 }}
                      >
                        {formatEnumLabel(status)}
                        {isCurrent ? (
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{ ml: 1, color: secondary, fontWeight: 700 }}
                          >
                            CURRENT
                          </Typography>
                        ) : null}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {STAGE_HINTS[status]}
                      </Typography>
                    </Box>

                    {useQuickButtons ? (
                      <Box display="flex" flexDirection="column" gap={0.5} flexShrink={0}>
                        <Box display="flex" gap={0.75} flexWrap="wrap">
                          {[0, 1].map((slot) => {
                            const approved = isSlotApproved(stageApprovals, slot);
                            const declined = isSlotDeclined(stageApprovals, slot);
                            const label = slot === 0 ? '1st Approval' : '2nd Approval';
                            let buttonLabel = label;
                            if (approved) buttonLabel = `${label} ✓`;
                            if (declined) buttonLabel = `${label} ✕ Declined`;
                            return (
                              <Button
                                key={slot}
                                size="small"
                                variant={approved || declined ? 'contained' : 'outlined'}
                                color={declined ? 'error' : 'secondary'}
                                disabled={readOnly}
                                onClick={() => setQuickApproval(key, slot)}
                                sx={{ textTransform: 'none', whiteSpace: 'nowrap', minWidth: 0, px: 1.25 }}
                              >
                                {buttonLabel}
                              </Button>
                            );
                          })}
                        </Box>
                        {stageApprovals.some(
                          (row, i) => i < 2 && (isSlotApproved(stageApprovals, i) || isSlotDeclined(stageApprovals, i))
                        ) ? (
                          <Box>
                            {[0, 1].map((slot) => {
                              const row = stageApprovals[slot];
                              if (!row || (!isSlotApproved(stageApprovals, slot) && !isSlotDeclined(stageApprovals, slot))) {
                                return null;
                              }
                              return (
                                <Typography
                                  key={`approval-detail-${slot}`}
                                  variant="caption"
                                  color={row.decision === 'DECLINED' ? 'error.main' : 'text.secondary'}
                                  display="block"
                                  sx={{ lineHeight: 1.35 }}
                                >
                                  {slot === 0 ? '1st' : '2nd'}: {row.decision === 'DECLINED' ? 'Declined' : 'Approved'}
                                  {' · '}
                                  {row.name || row.email}
                                  {row.email ? ` · ${row.email}` : ''}
                                  {row.approvedAt ? ` · ${row.approvedAt.replace('T', ' ')}` : ''}
                                  {row.comment ? ` · “${row.comment}”` : ''}
                                </Typography>
                              );
                            })}
                          </Box>
                        ) : null}
                      </Box>
                    ) : null}

                    {!useQuickButtons ? (
                      <TextField
                        type="date"
                        size="small"
                        label="Date"
                        value={dates[key] || ''}
                        onChange={(e) => onDateChange?.(key, e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        disabled={readOnly}
                        error={showDateError}
                        helperText={showDateError ? dateError : undefined}
                        sx={{
                          width: { xs: '100%', sm: compactControls ? controlWidth : 190 },
                          flexShrink: 0,
                          '& .MuiInputBase-root': {
                            height: compactControls ? 40 : undefined,
                            minHeight: compactControls ? 40 : undefined,
                            bgcolor: isDark ? 'background.paper' : '#fff',
                          },
                        }}
                      />
                    ) : null}
                  </Box>

                  {!useQuickButtons && useApprovalList ? (
                  <Box
                    sx={{
                      pl: { sm: 0.5 },
                      borderLeft: `2px solid ${alpha(theme.palette.divider, 0.9)}`,
                      ml: 0.5,
                    }}
                  >
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.75} gap={1}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Approvals ({stageApprovals.length})
                      </Typography>
                      {!readOnly ? (
                        <Button
                          size="small"
                          startIcon={<AddIcon fontSize="small" />}
                          onClick={() => addApproval(key)}
                          sx={{ textTransform: 'none', minWidth: 0 }}
                        >
                          Add approval
                        </Button>
                      ) : null}
                    </Box>

                    {stageApprovals.length === 0 ? (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        No approvals for this stage yet.
                      </Typography>
                    ) : (
                      <Box display="flex" flexDirection="column" gap={1}>
                        {stageApprovals.map((row) => (
                          <Box
                            key={row.key}
                            display="flex"
                            flexDirection={{ xs: 'column', sm: 'row' }}
                            gap={1}
                            alignItems={{ sm: 'center' }}
                          >
                            <TextField
                              size="small"
                              label="Name"
                              value={row.name || ''}
                              disabled={readOnly}
                              onChange={(e) => patchApproval(key, row.key, { name: e.target.value })}
                              sx={{ flex: 1, minWidth: 120 }}
                            />
                            <TextField
                              size="small"
                              label="Email"
                              type="email"
                              value={row.email || ''}
                              disabled={readOnly}
                              onChange={(e) => patchApproval(key, row.key, { email: e.target.value })}
                              sx={{ flex: 1, minWidth: 140 }}
                            />
                            <TextField
                              size="small"
                              label="Date & time"
                              type="datetime-local"
                              value={row.approvedAt || ''}
                              disabled={readOnly}
                              onChange={(e) =>
                                patchApproval(key, row.key, { approvedAt: e.target.value })
                              }
                              InputLabelProps={{ shrink: true }}
                              sx={{ width: { xs: '100%', sm: 210 }, flexShrink: 0 }}
                            />
                            {!readOnly ? (
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => removeApproval(key, row.key)}
                                aria-label="Remove approval"
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            ) : null}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                  ) : null}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
